import {
  ComparatorContext,
  getTypeFromQueryParams,
  IProduct,
  KiziLogger,
  parseBool,
  ProductType,
  useField,
  useFields,
  usePage,
} from '@kizi/comparator-core';
import {
  default as React,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getProductsWithReviews } from '../../../utils/product';
import { useReviews } from '../../hooks/reviews';
import { PageLayout, PageLayoutProps } from '../../PageLayout';
import { OverviewPageLoader } from '../../Pages/OverviewPageLoader';

export interface OfferPageProps extends PageLayoutProps {
  productsRequest: {
    url: string;
    options?: {
      method?: string;
      headers?: any;
      body?: any;
      queryParams?: { [key: string]: string };
    };
  };
  paramsFromFields: string[];
  defaultQueryParams?: {
    limit?: number;
    tariff?: string;
    sustainable?: string;
    housenumberaddition?: string;
    electricitycapacity?: number;
    gascapacity?: number;
    smartmeter?: number;
    customertype?: string;
    supplytype?: string;
    withsavings?: number;
    pricesexcludingtax?: boolean;
    hideunavailableproducts?: boolean;
    type?: string;
  };
}

export const OfferPage: React.FC<OfferPageProps> = ({
  productsRequest,
  content,
  defaultQueryParams = {},
  paramsFromFields,
  ...props
}) => {
  const { state, setState } = usePage();
  const fields = useFields(paramsFromFields ?? []);
  const [visibleProducts, setVisibleProducts] = useState<IProduct[]>([]);
  const isLoadingProducts = useField('overview.isLoadingProducts');
  const isLoadingPage = parseBool(isLoadingProducts?.value);
  const { api } = useContext(ComparatorContext);

  const { reviews, isLoading } = useReviews(
    getTypeFromQueryParams(paramsFromFields).toUpperCase() as ProductType,
  );

  useEffect(() => {
    isLoadingProducts?.set({ value: 'true' });
    const queryParams = {};
    for (const field of fields) {
      if (field) queryParams[field.name] = field.value ?? '';
    }

    let finalQueryParams = { ...defaultQueryParams, ...queryParams };

    api
      .getProducts(finalQueryParams, [], productsRequest.options)
      .then((_productsFromApi) => {
        const contractHandler = (products) =>
          products.filter(
            (p) =>
              p?.purchasableProduct !== false &&
              p?.notPurchasebleReason !== 'noContractsAvailable' &&
              (p?.availableContracts !== null || p?.availableContracts > 0),
          );

        isLoadingProducts?.set({ value: 'false' });
        setVisibleProducts(contractHandler(_productsFromApi));
      })
      .catch((error) => {
        KiziLogger.logError({
          eventId: 0,
          member: 'OfferPage',
          message: 'Error in useEffect ',
          eventName: 'getProductsError',
          data: {
            error,

            variables: {
              finalQueryParams,
              productsRequest: productsRequest.options,
            },
          },
        });
        isLoadingProducts?.set({ value: 'false' });
      });
  }, [fields]);

  const productsWithReviews: IProduct[] = useMemo(() => {
    if (isLoading || !reviews) {
      return visibleProducts;
    } else {
      return getProductsWithReviews(visibleProducts, reviews);
    }
  }, [visibleProducts, isLoading, reviews]);

  useEffect(() => {
    if (setState) {
      setState({
        ...state,
        productList: productsWithReviews,
      });
    }
  }, [productsWithReviews]);
  if (!setState) return null;

  if (isLoadingPage) {
    return <OverviewPageLoader />;
  }

  return <PageLayout {...props} content={content} />;
};
